import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { showCustomToast } from 'common/components/CustomToast/utils';
import { invoiceDateFormat } from 'common/utils/dateFormats';
import { differenceBy, intersection, isEmpty } from 'lodash';
import React, { useCallback, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';

import { ACCOUNTING_INITIAL_VALUES } from 'ap/signing/accountingEntry/view/constants';
import { AccountingDistributionRows } from 'ap/signing/accountingEntry/view/types';
import {
  InvoiceDetails,
  InvoiceDetailsVariables,
} from 'common/components/Modules/TransactionEdit/graphql/__generated__/InvoiceDetails';
import {
  AccountingViewRowValues,
  FileListArray,
  TransactionSigningValues,
} from 'common/components/Modules/TransactionEdit/types';
import { setUserDefaults } from 'utility/cache/ui';
import { getDefaultValues } from '../../../../common/components/Modules/TransactionEdit/utils';
import { validationSchema } from '../../../../common/components/Modules/TransactionEdit/validation';
import {
  EntityDeleteInput,
  InvoiceDistributionInput,
  InvoiceDistributionPatch,
  InvoiceDistributionUpdateTypeInput,
  InvoiceInput,
  InvoicePatch,
} from '../../../../common/types/globalTypes';
import { FormView } from './FormView';
import {
  InvoiceSigningCreateApply,
  InvoiceSigningCreateApplyVariables,
} from './__generated__/InvoiceSigningCreateApply';
import {
  InvoiceSigningUpdate,
  InvoiceSigningUpdateVariables,
} from './__generated__/InvoiceSigningUpdate';

import GET_INVOICE_DETAILS from '../../../../common/components/Modules/TransactionEdit/graphql/InvoiceDetails.graphql';
import UPDATE_INVOICE_SIGNING from './InvoiceSigningUpdate.graphql';
import CREATE_TRANSACTION from './InvoiceSigningCreateApply.graphql';

export const TransactionSigningView: React.FC<{
  itemId?: string;
  setTransactionId?: React.Dispatch<React.SetStateAction<string | undefined>>;
}> = ({ itemId, setTransactionId }) => {
  const { transactionSigningId } = useParams<{
    transactionSigningId: string | undefined;
  }>();
  const transactionSigningItemId = itemId ?? transactionSigningId;

  const userDefaults = useReactiveVar(setUserDefaults);
  const isNew = !transactionSigningItemId;
  const history = useHistory();

  const saveAnotherTransaction = useRef<boolean>(false);
  const fileListRef = useRef<FileListArray[] | undefined>(undefined);
  const {
    data: invoiceDetailsData,
    loading: invoiceDetailsLoading,
    refetch,
    error: invoiceDetailsDataError
  } = useQuery<InvoiceDetails, InvoiceDetailsVariables>(GET_INVOICE_DETAILS, {
    variables: {
      id: transactionSigningItemId!,
    },
    skip: !transactionSigningItemId,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  });

  const [createTransaction, { loading: createTransactionLoading }] =
    useMutation<InvoiceSigningCreateApply, InvoiceSigningCreateApplyVariables>(
      CREATE_TRANSACTION,
      { errorPolicy: 'all' }
    );

  const [updateTransaction, { loading: updateTransactionLoading }] =
    useMutation<InvoiceSigningUpdate, InvoiceSigningUpdateVariables>(
      UPDATE_INVOICE_SIGNING,
      { errorPolicy: 'all' }
    );

  const formMethods = useForm<TransactionSigningValues>({
    // mode: 'onChange',
    mode: 'all',
    resolver: yupResolver(validationSchema()),
  });

  const { handleSubmit, trigger, reset } = { ...formMethods };

  const onHandleSubmit = async (values: TransactionSigningValues) => {
    const initialValues = getDefaultValues({
      isNew,
      invoiceDetailsData,
      userDefaults,
    });
    const {
      controlTotalAmount,
      description,
      invoiceDate,
      reimburseAccountReference,
      reimburseAmount,
      invoiceNumber,
      corporatePeriodId,
      vendorReference,
      departmentId,
      statusHintOverride,
      statusHintOverrideId,
      transactionTypeId,
      currencyId,
      bankAccountNumber,
      bankIbanCode,
      bankName,
      bankRoutingNumber,
      bankSortCode,
      bankSwiftCode,
      bankAccountName,
      cardHolderId,
      invoiceDistributions,
      businessUnitId,
      entityDocuments,
      poInvoiceSchedules,
    } = values;

    const invoiceValues = {
      controlTotalAmount: !!controlTotalAmount
        ? parseFloat(controlTotalAmount).toFixed(2).toString()
        : '0.00',
      description,
      invoiceDate: invoiceDate ? invoiceDateFormat(invoiceDate) : null,
      reimburseAccountReference,
      reimburseAmount,
      invoiceNumber,
      corporatePeriodId,
      vendorReference,
      departmentId,
      statusHintOverride,
      statusHintOverrideId,
      transactionTypeId: parseInt(transactionTypeId?.toString() || '0'),
      currencyId: parseInt(currencyId?.toString() || '0'),
      bankAccountNumber,
      bankIbanCode,
      bankName,
      bankRoutingNumber,
      bankSortCode,
      bankSwiftCode,
      bankAccountName,
      // cardAccountId: cardHolderId,
      cardHolderId,
      businessUnitId,
    };
    const newInvoiceDistributionRows =
      invoiceDistributions &&
      (invoiceDistributions
        ?.filter((row) => {
          if (row.id) {
            return false;
          }
          const patch = Object.entries(row).reduce((res, [key, val]) => {
            if (
              (key === 'referenceCode6' &&
                val === userDefaults?.referenceCode6) ||
              (key === 'referenceCode7' &&
                val === userDefaults?.referenceCode7) ||
              (key === 'projectReference' &&
                val === userDefaults?.projectReference) ||
              (key === 'setReference' && val === userDefaults?.setReference) ||
              (key === '_accountName' &&
                val === userDefaults?.lookupAccounts?.lookupName)
            )
              return res;
            if (
              val &&
              val !==
                ACCOUNTING_INITIAL_VALUES[
                  key as keyof AccountingDistributionRows
                ]
            ) {
              return { ...res, [key]: val };
            }
            return res;
          }, {});

          return !isEmpty(patch);
        })
        .map(
          ({
            id,
            _rowTimestamp,
            _createdDate,
            _isDeletable,
            _isUpdatable,
            _ff1Name,
            _ff2Name,
            _ff3Name,
            _ff4Name,
            _accountName,
            ...addr
          }) => addr
        ) as InvoiceDistributionPatch[]);

    const createInvoiceDistributionRows =
      invoiceDistributions &&
      (invoiceDistributions
        ?.filter((row) => {
          if (row.id) {
            return false;
          }
          const patch = Object.entries(row).reduce((res, [key, val]) => {
            if (
              (key === 'referenceCode6' &&
                val === userDefaults?.referenceCode6) ||
              (key === 'referenceCode7' &&
                val === userDefaults?.referenceCode7) ||
              (key === 'projectReference' &&
                val === userDefaults?.projectReference) ||
              (key === 'setReference' && val === userDefaults?.setReference) ||
              (key === '_accountName' &&
                val === userDefaults?.lookupAccounts?.lookupName)
            )
              return res;
            if (
              val &&
              val !==
                ACCOUNTING_INITIAL_VALUES[
                  key as keyof AccountingDistributionRows
                ]
            ) {
              return { ...res, [key]: val };
            }
            return res;
          }, {});

          return !isEmpty(patch);
        })
        .map(
          ({
            id,
            _rowTimestamp,
            _createdDate,
            _isDeletable,
            _isUpdatable,
            tax1099T4TypeId,
            _ff1Name,
            _ff2Name,
            _ff3Name,
            _ff4Name,
            _accountName,
            poEntityDocumentId,
            isPoCharge,
            ...invoice
          }) => invoice
        ) as InvoiceDistributionInput[]);

    if (isNew) {
      const { data, errors } = await createTransaction({
        variables: {
          input: {
            invoice: invoiceValues as InvoiceInput,
            invoiceDistributions: createInvoiceDistributionRows,
            entityDocuments,
            poInvoiceSchedules,
          },
        },
        update: (cache, { data }) => {
          try {
            cache.modify({
              fields: {
                invoiceSigningSearchFilterTotals(existing) {
                  if (!existing) return existing;

                  if (existing.totalCount1 !== 0) {
                    const invoice = data?.invoiceSigningCreateApply?.invoice;
                    const controlTotalAmount = Number(
                      invoice?.controlTotalAmount ?? 0
                    );
                    const baseCurrencyAmount = Number(
                      invoice?._baseCurrencyAmount ?? 0
                    );
                    const spotCurrencyAmount = Number(
                      invoice?._spotCurrencyAmount ?? 0
                    );

                    const controlTotalAmountSum =
                      Number(existing.totalAmount1 ?? 0) + controlTotalAmount;
                    const baseCurrencyAmountSum =
                      Number(existing.totalAmount2 ?? 0) + baseCurrencyAmount;
                    const spotCurrencyAmountSum =
                      Number(existing.totalAmount3 ?? 0) + spotCurrencyAmount;

                    return {
                      totalAmount1: controlTotalAmountSum.toString(),
                      totalAmount2: baseCurrencyAmountSum.toString(),
                      totalAmount3: spotCurrencyAmountSum.toString(),
                      totalCount1: (existing.totalCount1 ?? 0) + 1,
                    };
                  } else {
                    return existing;
                  }
                },

                invoiceSigningSearch(existing, { readField, toReference }) {
                  const invoice = data?.invoiceSigningCreateApply?.invoice;
                  const pageInfo = existing.pageInfo;

                  if (!invoice) return existing;

                  const invoiceRef = toReference({
                    __typename: 'Invoice',
                    id: invoice.id,
                  });

                  return {
                    ...existing,
                    nodes: [invoiceRef, ...(existing.nodes ?? [])],
                    pageInfo: pageInfo!,
                  };
                },
              },
            });
          } catch (error) {}
        },
      });
      if (errors?.length) {
        showCustomToast({
          content: errors[0].message,
          appearance: 'error',
        });
      }
      if (data?.invoiceSigningCreateApply?.invoice) {
        if (!saveAnotherTransaction.current) {
          history.replace(
            `/signing/transaction/${data.invoiceSigningCreateApply?.invoice?.id}`
          );
        }
        showCustomToast({
          content: 'Transaction added successfully',
          appearance: 'success',
        });
        if (!fileListRef.current?.length) {
          showCustomToast({
            content: 'No documents attached while creating the transaction',
            appearance: 'warning',
          });
        }
        // setFileListArray([]);
        // setInitialDocumentMetaData(undefined);
      }
    } else {
      const invoicePatch: InvoicePatch = Object.entries(invoiceValues).reduce(
        (res, [key, val]) => {
          if (val !== initialValues[key as keyof TransactionSigningValues]) {
            return { ...res, [key]: val };
          }
          return res;
        },
        {}
      );

      const deletedInvoiceDistributionRows =
        initialValues.invoiceDistributions &&
        (differenceBy(
          initialValues.invoiceDistributions,
          invoiceDistributions || [],
          'id'
        ).map((addr) => ({
          id: addr.id!,
          rowTimestamp: addr._rowTimestamp!,
        })) as EntityDeleteInput[]);

      const updatedInvoiceDistributions =
        initialValues.invoiceDistributions &&
        invoiceDistributions &&
        intersection(
          initialValues.invoiceDistributions.map((addr) => addr.id),
          invoiceDistributions.filter((addr) => addr.id).map((addr) => addr.id)
        ).reduce((arr, targetId) => {
          const initialInvoiceDistributions =
            initialValues.invoiceDistributions!.find(
              (addr) => addr.id === targetId
            )!;
          const { id, _rowTimestamp, _accountName, ...updatedAddress } =
            invoiceDistributions!.find((addr) => addr.id === targetId)!;
          const patch = Object.entries(updatedAddress).reduce(
            (res, [key, val]) => {
              if (
                val !==
                initialInvoiceDistributions[
                  key as keyof AccountingViewRowValues
                ]
              )
                return { ...res, [key]: val };
              return res;
            },
            {}
          );
          if (!isEmpty(patch))
            return [
              ...arr,
              {
                id,
                rowTimestamp: _rowTimestamp,
                invoiceDistributionPatch: patch,
              },
            ];
          return arr;
        }, [] as InvoiceDistributionUpdateTypeInput[]);
      if (
        transactionSigningItemId &&
        invoiceDetailsData?.invoice?._rowTimestamp
      ) {
        const { errors } = await updateTransaction({
          variables: {
            input: {
              id: transactionSigningItemId!,
              rowTimestamp: invoiceDetailsData.invoice._rowTimestamp,
              invoiceDistributionsUpdate:
                updatedInvoiceDistributions &&
                updatedInvoiceDistributions.length > 0
                  ? updatedInvoiceDistributions
                  : undefined,
              invoiceDistributionsCreate:
                newInvoiceDistributionRows &&
                newInvoiceDistributionRows.length > 0
                  ? newInvoiceDistributionRows
                  : undefined,
              invoiceDistributionsDelete:
                deletedInvoiceDistributionRows &&
                deletedInvoiceDistributionRows.length > 0
                  ? deletedInvoiceDistributionRows
                  : undefined,
              invoicePatch: !isEmpty(invoicePatch) ? invoicePatch : undefined,
            },
          },
          update: (cache, { data }) => {
            try {
              cache.modify({
                fields: {
                  invoiceSigningSearchFilterTotals(existing) {
                    if (!existing) return existing;

                    const updatedInvoice = data?.invoiceSigningUpdate?.invoice;
                    const existingInvoice = invoiceDetailsData?.invoice;

                    if (!updatedInvoice || !existingInvoice) return existing;

                    const controlTotalDiff =
                      Number(updatedInvoice.controlTotalAmount ?? 0) -
                      Number(existingInvoice.controlTotalAmount ?? 0);
                    const baseCurrencyDiff =
                      Number(updatedInvoice._baseCurrencyAmount ?? 0) -
                      Number(existingInvoice._baseCurrencyAmount ?? 0);
                    const spotCurrencyDiff =
                      Number(updatedInvoice._spotCurrencyAmount ?? 0) -
                      Number(existingInvoice._spotCurrencyAmount ?? 0);

                    const anyChange =
                      controlTotalDiff !== 0 ||
                      baseCurrencyDiff !== 0 ||
                      spotCurrencyDiff !== 0;

                    if (anyChange) {
                      return {
                        totalAmount1: (
                          Number(existing.totalAmount1 ?? 0) + controlTotalDiff
                        ).toString(),
                        totalAmount2: (
                          Number(existing.totalAmount2 ?? 0) + baseCurrencyDiff
                        ).toString(),
                        totalAmount3: (
                          Number(existing.totalAmount3 ?? 0) + spotCurrencyDiff
                        ).toString(),
                        totalCount1: (existing.totalCount1 ?? 0) + 1,
                      };
                    }

                    return existing;
                  },
                },
              });
            } catch (error) {}
          },
          awaitRefetchQueries: true,
          refetchQueries: [
            {
              query: GET_INVOICE_DETAILS,
              variables: {
                id: transactionSigningItemId,
              },
            },
          ],
        });
        if (errors?.length) {
          showCustomToast({
            content: errors[0].message,
            appearance: 'error',
          });
        } else {
          showCustomToast({
            content: 'Transaction edited successfully',
            appearance: 'success',
          });
        }
      }
    }
  };
  const triggerCallBack = useCallback(trigger, []);

  useEffect(() => {
    triggerCallBack();
  }, [triggerCallBack]);

  useEffect(() => {
    const defaultValues = getDefaultValues({
      isNew,
      invoiceDetailsData,
      userDefaults,
    });

    reset(defaultValues);
  }, [invoiceDetailsData, isNew, reset, userDefaults]);

  return (
    <>
      <FormProvider {...formMethods}>
        <FormView
          onSave={handleSubmit(onHandleSubmit)}
          isSaveAnother={(value) => (saveAnotherTransaction.current = value)}
          isNew={isNew}
          isCreating={createTransactionLoading}
          isUpdating={updateTransactionLoading}
          invoiceDetailsData={invoiceDetailsData}
          dataLoading={invoiceDetailsLoading}
          refetch={refetch}
          onFileListArrayChange={(files) => (fileListRef.current = files)}
          setTransactionId={setTransactionId}
          invoiceDetailsDataError={invoiceDetailsDataError}
        />
      </FormProvider>
    </>
  );
};
